import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_STATUS } from "../../constants";
import { GetFacebookMedia } from "../../apis/posts/get_facebok_media";
import FacebookLogin from "./facebook_login";


export default function FacebookMedia({ mediaType, onSelectMedia }) {
    const navigate = useNavigate();
    const [mediaList, setMediaList] = useState([]);
    const [selectedMedia, setSelectedMedia] = useState([]);
    const [mediaApi, setMediaApi] = useState(API_STATUS);
    const [facebookLogin, setFacebookLogin] = useState(true);

    // Pagination State
    const [pagination, setPagination] = useState({
        has_next: false,
        after: null,
    });
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Initial fetch on mediaType change
    useEffect(() => {
        if (!facebookLogin) return;
        setMediaList([]);
        setSelectedMedia([]);
        setPagination({ has_next: false, after: null });

        const endpoint = mediaType === "videos" ? "get_videos" : "get_photos";
        GetFacebookMedia(setMediaApi, endpoint);
    }, [mediaType, facebookLogin]);

    // Handle API Response for both Photos and Videos
    useEffect(() => {
        if (!mediaApi.isLoader && !mediaApi.isError) {
            if (mediaApi.data != null) {
                const res = mediaApi.data;
                if (res["status"] === 2) {
                    setFacebookLogin(false);
                }
                if (res["status"] === 1 && Array.isArray(res["data"])) {
                    if (isLoadingMore) {
                        setMediaList((prev) => [...prev, ...res["data"]]);
                        setIsLoadingMore(false);
                    } else {
                        setMediaList(res["data"]);
                    }

                    if (res["pagination"]) {
                        setPagination({
                            has_next: res["pagination"]["has_next"] || false,
                            after: res["pagination"]["after"] || null,
                        });
                    }
                }
            }
        }
    }, [mediaApi]);

    // Load next page
    const handleLoadMore = () => {
        if (pagination.has_next && pagination.after) {
            setIsLoadingMore(true);
            const endpoint = mediaType === "videos" ? "get_videos" : "get_photos";
            GetFacebookMedia(setMediaApi, endpoint, pagination.after);
        }
    };

    // Toggle Selection (Max 5 for photos, Max 1 for videos)
    const handleToggleSelect = (item) => {
        setSelectedMedia((prevSelected) => {
            const isAlreadySelected = prevSelected.some((media) => media.id === item.id);
            let updatedSelection;

            if (isAlreadySelected) {
                updatedSelection = prevSelected.filter((media) => media.id !== item.id);
            } else {
                if (mediaType === "photos" && prevSelected.length >= 5) {
                    return prevSelected;
                }
                if (mediaType === "videos" && prevSelected.length >= 1) {
                    return prevSelected;
                }
                updatedSelection = [...prevSelected, item];
            }

            if (onSelectMedia) {
                onSelectMedia(updatedSelection);
            }

            return updatedSelection;
        });
    };

    // Helper to extract proper image source for both photos and videos
    const getThumbnailUrl = (item) => {
        if (item.source && mediaType === "photos") return item.source;
        return item.picture || item.source || (item.format && item.format[0]?.picture) || "";
    };

    return (
        <div className="w-full">
            {/* Header / Counter */}
            {
                facebookLogin &&

                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white capitalize">
                        Select {mediaType}
                    </h3>
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                        {selectedMedia.length} / {mediaType === "videos" ? "1" : "5"} selected
                    </span>
                </div>
            }

            {
                !facebookLogin && (
                    <FacebookLogin facebookLoginStatus={setFacebookLogin} />
                )
            }

            {/* Initial Loader */}
            {mediaApi.isLoader && !isLoadingMore && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                    {/* Dual-tone spinner with high-contrast track */}
                    <div className="relative w-10 h-10">
                        <div className="w-10 h-10 rounded-full border-4 border-slate-200 dark:border-slate-700"></div>
                        <div className="absolute top-0 left-0 w-10 h-10 rounded-full border-4 border-blue-600 border-t-transparent animate-spin"></div>
                    </div>
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                        Fetching Facebook {mediaType}...
                    </span>
                </div>
            )}

            {/* Error State */}
            {mediaApi.isError && (
                <div className="p-4 bg-red-50 text-red-600 rounded-lg text-center text-sm">
                    Failed to load Facebook {mediaType}. Please try again.
                </div>
            )}

            {/* Grid display */}
            {(!mediaApi.isLoader || isLoadingMore) && !mediaApi.isError && (
                <>
                    <div className="grid grid-cols-4 gap-4">
                        {mediaList.map((item) => {
                            const isSelected = selectedMedia.some((media) => media.id === item.id);
                            const thumbnailUrl = getThumbnailUrl(item);

                            return (
                                <div
                                    key={item.id}
                                    onClick={() => handleToggleSelect(item)}
                                    className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all duration-200 group bg-slate-900 ${isSelected
                                        ? "border-blue-600 ring-2 ring-blue-600/30 scale-[0.98]"
                                        : "border-transparent hover:border-slate-300 dark:hover:border-slate-300"
                                        }`}
                                >
                                    {/* Media Thumbnail */}
                                    {thumbnailUrl ? (
                                        <img
                                            src={thumbnailUrl}
                                            alt={item.name || item.description || "Facebook Media"}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-xs text-slate-400 p-2 text-center">
                                            No Preview
                                        </div>
                                    )}

                                    {/* Video Indicator Overlay */}
                                    {mediaType === "videos" && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors">
                                            <div className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white shadow-md">
                                                <svg className="w-5 h-5 fill-current ml-0.5" viewBox="0 0 24 24">
                                                    <path d="M8 5v14l11-7z" />
                                                </svg>
                                            </div>
                                        </div>
                                    )}

                                    {/* Checkmark Selection Overlay */}
                                    <div
                                        className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all z-10 ${isSelected
                                            ? "bg-blue-600 text-white shadow-md scale-100"
                                            : "bg-black/40 text-white opacity-0 group-hover:opacity-100 scale-90"
                                            }`}
                                    >
                                        <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                                            <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                                        </svg>
                                    </div>

                                    {/* Title / Caption Overlay */}
                                    {(item.name || item.description) && (
                                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2 z-10">
                                            <p className="text-xs text-white truncate">
                                                {item.name || item.description}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Pagination Button */}
                    {pagination.has_next && (
                        <div className="flex justify-center mt-6">
                            <button
                                onClick={handleLoadMore}
                                disabled={isLoadingMore}
                                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-all flex items-center gap-2 disabled:opacity-60"
                            >
                                {isLoadingMore ? (
                                    <>
                                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                                        Loading...
                                    </>
                                ) : (
                                    `Load More ${mediaType === "videos" ? "Videos" : "Photos"}`
                                )}
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Empty State */}
            {!mediaApi.isLoader && !mediaApi.isError && mediaList.length === 0 && facebookLogin && (
                <div className="text-center py-12 text-slate-400">
                    No {mediaType} found in your Facebook account.
                </div>
            )}
        </div>
    );
}